import { Trophy, Clock, Layers } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Initials, ProgressLine, Tag, attTone } from "@/components/kit";
import { ProofControl } from "@/components/Proof";
import { brl, fmtDate } from "@/lib/format";

const PRIO = { Alta: "dark", Média: "gold", Baixa: "muted" };

export function TaskList({ tasks, onToggle, people }) {
  if (!tasks.length) return <p className="py-6 text-sm text-neutral-400">Nenhuma tarefa por aqui.</p>;
  return (
    <ul className="divide-y divide-neutral-100">
      {tasks.map((t) => (
        <li key={t.id} data-testid={`task-${t.id}`}>
          <label className={`flex items-center gap-4 py-3.5 ${onToggle ? "cursor-pointer" : ""}`}>
          <Checkbox checked={t.done} disabled={!onToggle} onCheckedChange={() => onToggle?.(t.id)} data-testid={`task-toggle-${t.id}`} className="h-5 w-5 rounded-full border-gold" />
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium ${t.done ? "text-neutral-400 line-through" : "text-neutral-900"}`}>{t.title}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-neutral-500">
              <Clock className="h-3 w-3" /> {t.time}
              {people && ` · ${people[t.collaboratorId]}`}
            </p>
          </div>
          <Tag tone={PRIO[t.priority]}>{t.priority}</Tag>
          </label>
        </li>
      ))}
    </ul>
  );
}

export function ChecklistCard({ checklist, onToggle, onProof, onReview, actions }) {
  const operational = checklist.kind !== "administrativo";
  const mode = onProof ? "collab" : onReview ? "owner" : "view";
  const done = checklist.items.filter((i) => i.done).length;
  return (
    <div data-testid={`checklist-${checklist.id}`} className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-900">{checklist.title}</h3>
        <span className="flex items-center gap-1">
          <span data-testid={`checklist-count-${checklist.id}`} className="text-xs font-semibold text-gold-dark">{done}/{checklist.items.length}</span>
          {actions}
        </span>
      </div>
      <p className="mb-3 mt-0.5 text-xs text-neutral-400" data-testid={`checklist-kind-${checklist.id}`}>{checklist.period} · {operational ? <span className="font-medium text-gold-dark">Operacional · exige foto</span> : "Administrativo"}</p>
      <ProgressLine value={(done / checklist.items.length) * 100} />
      <ul className="mt-4 space-y-3">
        {checklist.items.map((it, i) => (
          <li key={it.text} className="flex items-start justify-between gap-2">
            <label className="flex flex-1 cursor-pointer items-start gap-3 text-sm">
              <Checkbox checked={it.done} disabled={!onToggle || !!it.proof || operational} onCheckedChange={() => onToggle?.(checklist.id, i)} data-testid={`checklist-item-${checklist.id}-${i}`} className="mt-0.5 border-gold" />
              <span className={it.done ? "text-neutral-400 line-through" : "text-neutral-700"}>{it.text}</span>
            </label>
            {(operational || it.proof) && <ProofControl item={it} mode={mode} companyId={checklist.companyId} requirePhoto={operational} testId={`proof-${checklist.id}-${i}`} onSave={(p) => onProof(checklist.id, i, p)} onReview={(s) => onReview(checklist.id, i, s)} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RankingTable({ rows, highlightId, positionOnly }) {
  return (
    <ul className="divide-y divide-neutral-100" data-testid="ranking-table">
      {rows.map((r) => (
        <li key={r.id} data-testid={`ranking-row-${r.id}`} className={`flex items-center gap-4 py-4 ${r.id === highlightId ? "-mx-3 rounded-lg bg-gold-soft px-3" : ""}`}>
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${r.position <= 3 ? "bg-gold text-neutral-900" : "bg-neutral-100 text-neutral-500"}`}>
            {r.position === 1 ? <Trophy className="h-4 w-4" /> : `${r.position}º`}
          </span>
          <Initials name={r.name} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-neutral-900">{r.name}</p>
            <p className="text-xs text-neutral-500">{r.role}</p>
          </div>
          {(!positionOnly || r.id === highlightId) && (<>
          <div className="hidden text-right sm:block">
            <p className="text-sm font-semibold text-neutral-900">{brl(r.sales)}</p>
            <p className="text-xs text-neutral-500">Tarefas {r.tasks}%</p>
          </div>
          <Tag tone={attTone(r.att)}>{r.att}% meta</Tag>
          <span className="w-12 text-right font-display text-lg font-semibold text-neutral-900">{r.score}</span>
          </>)}
        </li>
      ))}
    </ul>
  );
}

const STATUS = { Ativo: "green", "Em revisão": "gold", Rascunho: "muted" };

export function ProcessList({ processes }) {
  return (
    <Accordion type="single" collapsible className="w-full" data-testid="process-list">
      {processes.map((p) => (
        <AccordionItem key={p.id} value={p.id} className="border-neutral-100">
          <AccordionTrigger data-testid={`process-${p.id}`} className="py-4 text-left hover:no-underline">
            <div className="flex flex-1 flex-col gap-1 pr-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-center gap-3 text-sm font-semibold text-neutral-900">
                <Layers className="h-4 w-4 shrink-0 text-gold" /> {p.name}
              </span>
              <span className="flex gap-2 pl-7 sm:pl-0">
                <Tag>{p.area}</Tag>
                <Tag tone={STATUS[p.status]}>{p.status}</Tag>
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <ol className="ml-2 space-y-3 border-l border-gold/40 pl-6">
              {p.steps.map((s, i) => (
                <li key={s} className="relative text-sm text-neutral-700">
                  <span className="absolute -left-[31px] flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-gold-dark ring-1 ring-gold">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <p className="mt-4 text-xs text-neutral-400">Responsável: {p.owner}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

const FB_TONE = { Elogio: "green", Desenvolvimento: "gold", Reconhecimento: "dark" };

export function FeedbackItem({ fb, toName }) {
  return (
    <article data-testid={`feedback-${fb.id}`} className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tag tone={FB_TONE[fb.type]}>{fb.type}</Tag>
        <span className="text-xs text-neutral-400">{fmtDate(fb.date)}</span>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-neutral-700">{fb.text}</p>
      <p className="mt-4 text-xs text-neutral-500">
        De <span className="font-semibold text-neutral-800">{fb.from}</span>
        {toName && <> para <span className="font-semibold text-neutral-800">{toName}</span></>}
      </p>
    </article>
  );
}
